import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogBody, FormDialogFooter } from '../../components/ui/form-dialog';
import React, { useEffect, useState } from 'react';
import { Flame, AlertCircle, Save } from 'lucide-react';
import type { CelulaKanbanEntity, EstufaEntity } from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../../lib/csrf-fetch';
import { Checkbox } from '../../components/ui/checkbox';
import { FormMessage } from '../../components/ui/form-message';
import { Label } from '../../components/ui/label';
import { LimitesWipEditor } from './limites-wip-editor';
import {
  deLinhas,
  manterCabinesVisiveis,
  paraLinhas,
  podeGravarFormulario,
  validarFormularioEstufa,
  type ErroFormularioEstufa,
  type LinhaLimiteWip,
} from '../../lib/kanban/cadastro-kanban';

interface EstufaFormProps {
  /** null = estufa nova. */
  estufa: EstufaEntity | null;
  podeEditar: boolean;
  onClose: () => void;
  onSalvo: () => void;
}

const CONTROLE_CLASSE =
  'w-full h-8 px-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white disabled:opacity-60';

async function mensagemDoServidor(res: Response, padrao: string): Promise<string> {
  const data = (await res.json().catch(() => ({}))) as { message?: string | string[] };
  const mensagem = Array.isArray(data.message) ? data.message.join(' ') : data.message;
  return mensagem || padrao;
}

function idDoCampoComErro(erro: ErroFormularioEstufa): string {
  switch (erro.campo) {
    case 'codigo':
      return 'estufa-codigo';
    case 'nome':
      return 'estufa-nome';
    case 'cabines':
      return 'estufa-cabine-0';
    default:
      return `estufa-limite-${erro.indice ?? 0}`;
  }
}

/**
 * Cadastro de Estufa (D-03, KAN-10, KAN-11, KAN-04): codigo, nome, ativa, cabines e limites de
 * WIP. Tudo segue numa unica requisicao, gravada de forma atomica pela API. Vincular cabines so
 * muda a agregacao e os avisos do quadro; nunca move cartoes.
 */
export const EstufaForm: React.FC<EstufaFormProps> = ({ estufa, podeEditar, onClose, onSalvo }) => {
  const nova = estufa === null;
  const [codigo, setCodigo] = useState(estufa?.codigo ?? '');
  const [nome, setNome] = useState(estufa?.nome ?? '');
  const [ativa, setAtiva] = useState<boolean>(estufa?.ativo ?? true);
  const [linhas, setLinhas] = useState<LinhaLimiteWip[]>(() => paraLinhas(estufa?.limites ?? []));
  const [cabinesMarcadas, setCabinesMarcadas] = useState<string[]>([...(estufa?.cabines ?? [])]);
  const [erroForm, setErroForm] = useState<ErroFormularioEstufa | null>(null);
  const [celulasCabine, setCelulasCabine] = useState<CelulaKanbanEntity[]>([]);
  const [estufas, setEstufas] = useState<EstufaEntity[]>([]);
  const [loading, setLoading] = useState(true);
  // WR-GAP-01: sem a carga completa das cabines/estufas o formulario nao e gravavel.
  const [cargaOk, setCargaOk] = useState(false);
  const [tentativaCarga, setTentativaCarga] = useState(0);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    const carregar = async () => {
      setLoading(true);
      setCargaOk(false);
      try {
        const [resCelulas, resEstufas] = await Promise.all([
          apiFetch<{ data: CelulaKanbanEntity[] }>('/api/v1/planejamento/celulas?apenasAtivas=false'),
          apiFetch<{ data: EstufaEntity[] }>('/api/v1/planejamento/estufas?apenasAtivas=false'),
        ]);
        if (!vivo) return;
        const cabines = (resCelulas?.data ?? []).filter((c) => c.ehCabine);
        setCelulasCabine([...cabines].sort((a, b) => a.codigo.localeCompare(b.codigo)));
        // WR-GAP-03: codigo vinculado que nao e cabine (legado) nao tem checkbox; descarta.
        const visiveis = cabines.map((c) => c.codigo);
        setCabinesMarcadas((atual) => manterCabinesVisiveis(atual, visiveis));
        setEstufas(resEstufas?.data ?? []);
        setErro(null);
        setCargaOk(true);
      } catch {
        if (vivo) setErro('Falha ao carregar as cabines e as estufas.');
      } finally {
        if (vivo) setLoading(false);
      }
    };
    void carregar();
    return () => {
      vivo = false;
    };
  }, [tentativaCarga]);

  const codigoDaEstufa = new Map(estufas.map((e) => [e.id, e.codigo]));

  const alternarCabine = (codigoCabine: string, marcada: boolean) => {
    setErroForm(null);
    setCabinesMarcadas((atual) => {
      const semCabine = atual.filter((c) => c !== codigoCabine);
      return marcada ? [...semCabine, codigoCabine] : semCabine;
    });
  };

  const handleSalvar = async () => {
    if (!podeGravarFormulario({ loading, cargaOk, saving })) return;
    setErro(null);
    // Erro junto do campo, foco no primeiro campo invalido e Salvar sempre habilitado.
    const invalido = validarFormularioEstufa(
      { codigo, nome, ativo: ativa, cabines: cabinesMarcadas, linhas },
      nova ? 'criar' : 'editar',
    );
    setErroForm(invalido);
    if (invalido) {
      document.getElementById(idDoCampoComErro(invalido))?.focus();
      return;
    }

    setSaving(true);
    try {
      const cabines = [...cabinesMarcadas].sort((a, b) => a.localeCompare(b));
      const limites = deLinhas(linhas);
      const res = nova
        ? await apiMutate('/api/v1/planejamento/estufas', {
            method: 'POST',
            body: { codigo: codigo.trim(), nome: nome.trim(), ativo: ativa, cabines, limites },
          })
        : await apiMutate(`/api/v1/planejamento/estufas/${estufa.id}`, {
            method: 'PATCH',
            body: { nome: nome.trim(), ativo: ativa, cabines, limites },
          });
      if (!res.ok) throw new Error(await mensagemDoServidor(res, 'Erro ao salvar a estufa'));
      onSalvo();
    } catch (err) {
      setErro(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <FormDialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <FormDialogContent aria-describedby={undefined}>
        <FormDialogHeader>
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-slate-600" />
            <FormDialogTitle>{nova ? 'Nova Estufa' : 'Editar Estufa'}</FormDialogTitle>
          </div>

        </FormDialogHeader>

        {/* WR-GAP-04: fora da area rolavel, o erro do servidor fica sempre visivel junto do Salvar. */}
        {erro && (
          <div
            role="alert"
            className="mx-5 mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-700 flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        <FormDialogBody className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-sm text-slate-500">Carregando...</div>
          ) : !cargaOk ? (
            <div className="text-center py-6">
              <button
                type="button"
                onClick={() => {
                  setErro(null);
                  setTentativaCarga((n) => n + 1);
                }}
                className="px-4 py-2 text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-md transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-1">
                <Label htmlFor="estufa-codigo" className="text-xs font-semibold text-slate-700">
                  Código
                </Label>
                <input
                  id="estufa-codigo"
                  type="text"
                  value={codigo}
                  readOnly={!nova}
                  disabled={!podeEditar}
                  placeholder="Ex.: E"
                  aria-invalid={erroForm?.campo === 'codigo' || undefined}
                  aria-describedby={erroForm?.campo === 'codigo' ? 'estufa-codigo-erro' : undefined}
                  onChange={(e) => {
                    setErroForm(null);
                    setCodigo(e.target.value);
                  }}
                  className={`${CONTROLE_CLASSE} ${nova ? '' : 'bg-slate-100'}`}
                />
                {erroForm?.campo === 'codigo' && <FormMessage id="estufa-codigo-erro">{erroForm.mensagem}</FormMessage>}
              </div>

              <div className="space-y-1">
                <Label htmlFor="estufa-nome" className="text-xs font-semibold text-slate-700">
                  Nome
                </Label>
                <input
                  id="estufa-nome"
                  type="text"
                  value={nome}
                  disabled={!podeEditar}
                  placeholder="Ex.: Estufa E"
                  aria-invalid={erroForm?.campo === 'nome' || undefined}
                  aria-describedby={erroForm?.campo === 'nome' ? 'estufa-nome-erro' : undefined}
                  onChange={(e) => {
                    setErroForm(null);
                    setNome(e.target.value);
                  }}
                  className={CONTROLE_CLASSE}
                />
                {erroForm?.campo === 'nome' && <FormMessage id="estufa-nome-erro">{erroForm.mensagem}</FormMessage>}
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="estufa-ativa"
                    checked={ativa}
                    disabled={!podeEditar}
                    onCheckedChange={(marcada) => {
                      setErroForm(null);
                      setAtiva(marcada === true);
                    }}
                  />
                  <Label htmlFor="estufa-ativa" className="text-xs font-semibold text-slate-700">
                    Estufa ativa
                  </Label>
                </div>
                <p className="text-[11px] text-slate-500">Para desativar, desmarque todas as cabines.</p>
              </div>

              <fieldset
                className="space-y-2"
                aria-describedby={erroForm?.campo === 'cabines' ? 'estufa-cabines-erro' : undefined}
              >
                <legend className="text-xs font-semibold text-slate-700">Cabines vinculadas</legend>
                {celulasCabine.length === 0 ? (
                  <p className="text-[11px] text-slate-500">
                    Nenhuma célula marcada como cabine. Marque em Células → Kanban.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {celulasCabine.map((celula, indice) => {
                      const idCabine = `estufa-cabine-${indice}`;
                      const outraEstufa =
                        celula.estufaId && celula.estufaId !== estufa?.id ? codigoDaEstufa.get(celula.estufaId) : null;
                      return (
                        <div key={celula.id} className="flex items-center gap-2">
                          <Checkbox
                            id={idCabine}
                            checked={cabinesMarcadas.includes(celula.codigo)}
                            disabled={!podeEditar}
                            aria-invalid={erroForm?.campo === 'cabines' || undefined}
                            onCheckedChange={(marcada) => alternarCabine(celula.codigo, marcada === true)}
                          />
                          <Label htmlFor={idCabine} className="text-xs text-slate-700">
                            {celula.codigo} - {celula.nome}
                            {celula.ativo ? '' : ' (inativa)'}
                            {outraEstufa ? (
                              <span className="ml-1 text-[11px] text-amber-700">hoje na Estufa {outraEstufa}</span>
                            ) : null}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
                {cabinesMarcadas.length === 0 && (
                  <p className="text-[11px] text-slate-500">Nenhuma cabine vinculada.</p>
                )}
                {erroForm?.campo === 'cabines' && (
                  <FormMessage id="estufa-cabines-erro">{erroForm.mensagem}</FormMessage>
                )}
              </fieldset>

              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-800 border-b pb-2">Limites de WIP</h3>
                <p className="text-[11px] text-slate-500">
                  O limite da estufa vale para a soma das cabines vinculadas e só avisa; nunca bloqueia.
                </p>
                <LimitesWipEditor
                  idPrefixo="estufa"
                  linhas={linhas}
                  onChange={(novas) => {
                    setErroForm(null);
                    setLinhas(novas);
                  }}
                  erro={
                    erroForm?.campo === 'limites'
                      ? { indice: erroForm.indice ?? 0, mensagem: erroForm.mensagem }
                      : null
                  }
                  desabilitado={!podeEditar}
                  textoSemLimite="Sem limite de WIP: a estufa nunca é sinalizada como sobrecarregada."
                />
              </section>
            </div>
          )}
        </FormDialogBody>

        <FormDialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-md transition-colors"
          >
            {podeEditar ? 'Cancelar' : 'Fechar'}
          </button>
          {podeEditar && (
            <button
              type="button"
              onClick={() => void handleSalvar()}
              disabled={!podeGravarFormulario({ loading, cargaOk, saving })}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-xs font-semibold hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Salvando...' : nova ? 'Criar estufa' : 'Salvar alterações'}
            </button>
          )}
        </FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
};
